import { createSelector } from 'reselect';

import {
    PaymentClientMethod,
    PaymentCreditCard,
    PaymentDirectDebit,
    PaymentMethod,
    PaymentPaypal,
    PaymentType,
} from '@packlink/packlink-sdk';

import { IAppState } from '@store/reducers';

const findPaymentMethodBy = (
    payments: (PaymentClientMethod | undefined)[] | null,
    compareFn: (payment: PaymentClientMethod | undefined) => boolean,
): PaymentClientMethod | null => {
    const paymentFound = payments && payments.find(compareFn);
    return paymentFound || null;
};

const isDirect = (payment?: PaymentClientMethod): boolean => payment?.type === PaymentType.DIRECT;
const isDeferred = (payment?: PaymentClientMethod): boolean => payment?.type === PaymentType.DEFERRED;
const isCreditCard = (payment?: PaymentClientMethod): boolean => payment?.method === PaymentMethod.CREDITCARD;
const isPaypal = (payment?: PaymentClientMethod): boolean => payment?.method === PaymentMethod.PAYPAL;

const isDirectPaypal = (payment?: PaymentClientMethod): boolean => isDirect(payment) && isPaypal(payment);
const isDirectCreditCard = (payment?: PaymentClientMethod): boolean => isDirect(payment) && isCreditCard(payment);

export const getClientPaymentMethods = (state: IAppState) => {
    const clientMethods = state.payment.clientMethods;

    if (clientMethods) {
        return clientMethods.map((clientMethod): PaymentCreditCard | PaymentDirectDebit | PaymentPaypal | undefined => {
            if (
                clientMethod.method === PaymentMethod.CREDITCARD ||
                clientMethod.method === PaymentMethod.CREDITCARD3DSECURE
            ) {
                return PaymentCreditCard.deserialize(clientMethod);
            }

            if (clientMethod.method === PaymentMethod.PAYPAL) {
                return PaymentPaypal.deserialize(clientMethod);
            }

            if (clientMethod.method === PaymentMethod.DIRECTDEBIT) {
                return PaymentDirectDebit.deserialize(clientMethod);
            }

            if (clientMethod.method === PaymentMethod.FREE) {
                return PaymentDirectDebit.deserialize(clientMethod);
            }

            return undefined;
        });
    }

    return null;
};

export const getDeferredPaymentMethodState = createSelector([getClientPaymentMethods], (paymentMethods) =>
    findPaymentMethodBy(paymentMethods, isDeferred),
);

export const getClientCreditCardPaymentMethod = createSelector(
    [getClientPaymentMethods],
    (paymentMethods) => findPaymentMethodBy(paymentMethods, isDirectCreditCard) as PaymentCreditCard | null,
);

export const getClientPaypalPaymentMethod = createSelector(
    [getClientPaymentMethods],
    (paymentMethods) => findPaymentMethodBy(paymentMethods, isDirectPaypal) as PaymentPaypal | null,
);
